import * as React from "react";
import {useCallback, useMemo, useRef, useState} from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {useCreateEmailListMutation} from "../../../services/emailListApi";

export type FileImportUploaderProps = {
  onFiles?: (files: File[]) => void;
  accept?: string;          // e.g. ".csv,.xlsx,.txt"
  exampleHref?: string;
  exampleText?: string;     // e.g. "Download example file (.csv)"
  minHeight?: number;
};

export default function FileImportUploader({
                                             onFiles,
                                             accept = ".csv,.xlsx,.txt",
                                             exampleHref = "/example.csv",
                                             exampleText = "Download example file (.csv)",
                                             minHeight = 220
                                           }: FileImportUploaderProps) {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [picked, setPicked] = useState<File[]>([]);
  const [title, setTitle] = useState("");
  const [open, setOpen] = useState(false);

  const [createEmailList, {isLoading}] = useCreateEmailListMutation();

  const borderColor = dragActive ? theme.palette.primary.main : theme.palette.divider;
  const bg = dragActive
    ? (theme.palette.mode === "light" ? "#F6FAFF" : "rgba(33,150,243,0.08)")
    : "transparent";

  const handleBrowse = () => inputRef.current?.click();

  const handleFiles = useCallback((filesLike: FileList | File[]) => {
    const files = Array.from(filesLike);
    setPicked(files);
    onFiles?.(files);
  }, [onFiles]);

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) {
      handleFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  const pickedLabel = useMemo(() => {
    if (!picked.length) return "";
    if (picked.length === 1) return picked[0].name;
    return `${picked.length} files selected`;
  }, [picked]);

  const canUpload = title.trim().length > 0 && picked.length > 0 && !isLoading;

  const handleUpload = async () => {
    if (!canUpload) return;
    try {
      const resId = await createEmailList({title: title.trim(), file: picked[0]}).unwrap();
      console.log("Created list id:", resId.id);
    } catch (e) {
      console.error("Upload failed:", e);
    }
    setTitle("");
    setOpen(false);
    setPicked([]);
  };

  return (
    <Box>
      <Button variant="contained" onClick={() => setOpen(true)}>
        Upload new email list
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload new email list</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            {/* 1) Name */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">1) Choose name</Typography>
            </Stack>
            <TextField
              required
              label="Target email list"
              placeholder="e.g. Newsletter Subscribers"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isLoading}
            />

            {/* 2) File */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">2) Upload your file</Typography>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              Select a file containing your contacts to import.
            </Typography>

            {/* Drop area */}
            <Box
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={handleBrowse}
              role="button"
              aria-label="Upload area"
              tabIndex={0}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleBrowse()}
              sx={{
                border: `2px dashed ${borderColor}`,
                borderRadius: 2,
                minHeight,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: bg,
                transition: "all .15s ease",
                cursor: "pointer",
                px: 2
              }}
            >
              <Stack spacing={1} alignItems="center" textAlign="center">
                <CloudUploadIcon sx={{fontSize: 56, opacity: 0.7}}/>
                <Typography variant="subtitle1">
                  Select your file or drag and drop it here
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  .csv, .xlsx or .txt
                </Typography>
                {pickedLabel && (
                  <Typography variant="body2" sx={{mt: 1}}>
                    Chosen: <strong>{pickedLabel}</strong>
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  sx={{mt: 1}}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBrowse();
                  }}
                  disabled={isLoading}
                >
                  Browse files
                </Button>
                <input
                  ref={inputRef}
                  type="file"
                  accept={accept}
                  hidden
                  onChange={(e) => e.target.files && handleFiles(e.target.files)}
                />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        {/* Link left, buttons right */}
        <DialogActions sx={{justifyContent: "unset"}}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{width: "100%"}}>
            <Link href={exampleHref} underline="hover" target="_blank" rel="noopener">
              {exampleText}
            </Link>
            <Box sx={{flexGrow: 1}}/>
            <Button onClick={() => setOpen(false)} disabled={isLoading}>Cancel</Button>
            <Button
              onClick={handleUpload}
              variant="contained"
              disabled={!canUpload}
            >
              Upload
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
